/**
 * SettingsView - Device settings management
 * 
 * Features:
 * - Local settings via DeviceSettingsContext (UI preferences)
 * - Backend settings sync (senior profile preferences)
 * - Real-time save to backend
 * - Visual feedback for all changes
 */
import React, { useState, useEffect } from 'react';
import { Settings, Volume2, Type, Palette, Globe, Mic, Clock, Moon, Save } from 'lucide-react';
import { useDeviceSettings } from '../../contexts/DeviceSettingsContext';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SettingsView({ senior, onSettingsUpdated }) {
  const { settings, updateSetting, resetSettings } = useDeviceSettings();
  
  // Backend settings state
  const [backendSettings, setBackendSettings] = useState({
    language: 'de',
    religion: 'none',
    prayer_type: 'none',
    text_size: 'normal',
    high_contrast: false,
    quiet_hours: {
      from: '22:00',
      to: '07:00'
    }
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Load backend settings on mount
  useEffect(() => {
    if (senior?.id) {
      loadBackendSettings();
    }
  }, [senior?.id]);
  
  const loadBackendSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/seniors/${senior.id}`);
      const data = response.data;
      
      setBackendSettings({
        language: data.preferences?.language || data.language || 'de',
        religion: data.religion || data.preferences?.religion || 'none',
        prayer_type: data.preferences?.prayer_type || data.religion || 'none',
        text_size: data.preferences?.text_size || data.preferences?.font_size || 'normal',
        high_contrast: data.preferences?.high_contrast || false,
        quiet_hours: {
          from: data.preferences?.quiet_hours?.from || '22:00',
          to: data.preferences?.quiet_hours?.to || '07:00'
        }
      });
      
      console.log('[SettingsView] Loaded backend settings:', data.preferences);
    } catch (error) {
      console.error('[SettingsView] Error loading settings:', error);
    }
  };
  
  const saveToBackend = async () => {
    if (!senior?.id) {
      toast.error('Kein Senior-Profil gefunden');
      return;
    }
    
    try {
      setSaving(true);
      
      const updatePayload = {
        preferences: {
          language: backendSettings.language,
          religion: backendSettings.religion,
          prayer_type: backendSettings.prayer_type,
          text_size: backendSettings.text_size,
          high_contrast: backendSettings.high_contrast,
          quiet_hours: backendSettings.quiet_hours
        }
      };
      
      const response = await axios.patch(
        `${API}/api/seniors/${senior.id}`,
        updatePayload
      );
      
      console.log('[SettingsView] Settings saved:', response.data);
      toast.success('Einstellungen gespeichert');
      setHasChanges(false);
      
      // Notify parent component
      if (onSettingsUpdated) {
        onSettingsUpdated(response.data.senior);
      }
      
    } catch (error) {
      console.error('[SettingsView] Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  
  const handleBackendSettingChange = (key, value) => {
    setBackendSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };
  
  const handleQuietHoursChange = (key, value) => {
    setBackendSettings(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleFontSizeChange = (size) => {
    updateSetting('fontSize', size);
    toast.success(`Schriftgröße: ${getFontSizeLabel(size)}`);
  };

  const handleContrastToggle = () => {
    const newContrast = settings.contrast === 'normal' ? 'high' : 'normal';
    updateSetting('contrast', newContrast);
    toast.success(newContrast === 'high' ? 'Hoher Kontrast aktiviert' : 'Normaler Kontrast aktiviert');
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    updateSetting('volume', newVolume);
  };

  const handleLanguageChange = (lang) => {
    updateSetting('language', lang);
    toast.success(lang === 'de' ? 'Sprache: Deutsch' : 'Sprache: Türkçe');
  };

  const handleMicrophoneToggle = () => {
    const newState = !settings.microphone;
    updateSetting('microphone', newState);
    toast.success(newState ? 'Mikrofon aktiviert' : 'Mikrofon deaktiviert');
  };

  const getFontSizeLabel = (size) => {
    const labels = {
      small: 'Klein',
      medium: 'Mittel',
      large: 'Groß',
      xlarge: 'Sehr Groß'
    };
    return labels[size] || size;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="w-10 h-10 text-gray-600" />
          Einstellungen
        </h1>
        
        {/* Save Button (only show if backend changes) */}
        {hasChanges && (
          <button
            onClick={saveToBackend}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Speichern
              </>
            )}
          </button>
        )}
      </div>

      <div className="space-y-6">
        
        {/* SECTION: Appearance & Accessibility */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">👁️ Darstellung & Barrierefreiheit</h2>
        </div>
        {/* Font Size */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Type className="w-8 h-8 text-gray-600" />
            <h2 className="text-2xl font-bold text-gray-900">Schriftgröße</h2>
          </div>
          
          <div className="grid grid-cols-4 gap-3">
            {['small', 'medium', 'large', 'xlarge'].map((size) => (
              <button
                key={size}
                onClick={() => handleFontSizeChange(size)}
                className={`p-4 rounded-xl text-center font-bold transition-all ${
                  settings.fontSize === size
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className={`${
                  size === 'small' ? 'text-sm' :
                  size === 'medium' ? 'text-base' :
                  size === 'large' ? 'text-xl' :
                  'text-2xl'
                }`}>
                  {getFontSizeLabel(size)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Contrast Mode */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Palette className="w-8 h-8 text-gray-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Kontrastmodus</h2>
                <p className="text-lg text-gray-600">Bessere Lesbarkeit bei schlechtem Licht</p>
              </div>
            </div>
            
            <button
              onClick={handleContrastToggle}
              className={`relative w-20 h-10 rounded-full transition-colors ${
                settings.contrast === 'high' ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full transition-transform ${
                settings.contrast === 'high' ? 'translate-x-10' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>

        {/* Volume */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Volume2 className="w-8 h-8 text-gray-600" />
            <h2 className="text-2xl font-bold text-gray-900">Lautstärke</h2>
          </div>
          
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={settings.volume}
              onChange={handleVolumeChange}
              className="flex-1 h-3 bg-gray-200 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 ${settings.volume * 100}%, #e5e7eb ${settings.volume * 100}%)`
              }}
            />
            <span className="text-2xl font-bold text-gray-900 w-16 text-right">
              {Math.round(settings.volume * 100)}%
            </span>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-8 h-8 text-gray-600" />
            <h2 className="text-2xl font-bold text-gray-900">Sprache</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleLanguageChange('de')}
              className={`p-6 rounded-xl text-center font-bold transition-all ${
                settings.language === 'de'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="text-4xl mb-2">🇩🇪</div>
              <div className="text-xl">Deutsch</div>
            </button>
            
            <button
              onClick={() => handleLanguageChange('tr')}
              className={`p-6 rounded-xl text-center font-bold transition-all ${
                settings.language === 'tr'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="text-4xl mb-2">🇹🇷</div>
              <div className="text-xl">Türkçe</div>
            </button>
          </div>
        </div>

        {/* Microphone */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mic className="w-8 h-8 text-gray-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Sprachsteuerung</h2>
                <p className="text-lg text-gray-600">Gerät mit Stimme bedienen</p>
              </div>
            </div>
            
            <button
              onClick={handleMicrophoneToggle}
              className={`relative w-20 h-10 rounded-full transition-colors ${
                settings.microphone ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full transition-transform ${
                settings.microphone ? 'translate-x-10' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
        
        {/* SECTION: Profile Settings (Backend) */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-4 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">⚙️ Profil-Einstellungen</h2>
          <p className="text-gray-600">Diese Einstellungen werden in Ihrem Profil gespeichert</p>
        </div>
        
        {/* Backend: Language */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-8 h-8 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900">Bevorzugte Sprache</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
              { value: 'tr', label: 'Türkçe', flag: '🇹🇷' }
            ].map((lang) => (
              <button
                key={lang.value}
                onClick={() => handleBackendSettingChange('language', lang.value)}
                className={`p-6 rounded-xl text-center font-bold transition-all ${
                  backendSettings.language === lang.value
                    ? 'bg-indigo-500 text-white shadow-lg ring-4 ring-indigo-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="text-4xl mb-2">{lang.flag}</div>
                <div className="text-xl">{lang.label}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Backend: Religion & Prayer Times */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Moon className="w-8 h-8 text-purple-600" />
            <h2 className="text-2xl font-bold text-gray-900">Religion & Gebetszeiten</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">
                Religion
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'none', label: 'Keine', icon: '⚪' },
                  { value: 'islam', label: 'Islam', icon: '☪️' },
                  { value: 'christian', label: 'Christlich', icon: '✝️' }
                ].map((rel) => (
                  <button
                    key={rel.value}
                    onClick={() => {
                      handleBackendSettingChange('religion', rel.value);
                      handleBackendSettingChange('prayer_type', rel.value);
                    }}
                    className={`p-4 rounded-xl text-center font-bold transition-all ${
                      backendSettings.religion === rel.value
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="text-3xl mb-1">{rel.icon}</div>
                    <div className="text-lg">{rel.label}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {backendSettings.religion !== 'none' && (
              <div className="p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                <p className="text-gray-700">
                  ✨ Gebetszeiten werden automatisch basierend auf Ihrem Standort angezeigt
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Backend: Text Size (Synced) */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Type className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Bevorzugte Textgröße</h2>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'normal', label: 'Normal' },
              { value: 'large', label: 'Groß' },
              { value: 'xlarge', label: 'Sehr Groß' }
            ].map((size) => (
              <button
                key={size.value}
                onClick={() => handleBackendSettingChange('text_size', size.value)}
                className={`p-6 rounded-xl text-center font-bold transition-all ${
                  backendSettings.text_size === size.value
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className={`${
                  size.value === 'normal' ? 'text-base' :
                  size.value === 'large' ? 'text-xl' :
                  'text-2xl'
                } font-bold`}>
                  {size.label}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Backend: High Contrast */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Palette className="w-8 h-8 text-orange-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Hoher Kontrast (Profil)</h2>
                <p className="text-lg text-gray-600">Gespeichert in Ihrem Profil</p>
              </div>
            </div>
            
            <button
              onClick={() => handleBackendSettingChange('high_contrast', !backendSettings.high_contrast)}
              className={`relative w-20 h-10 rounded-full transition-colors ${
                backendSettings.high_contrast ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full transition-transform ${
                backendSettings.high_contrast ? 'translate-x-10' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
        
        {/* Backend: Quiet Hours */}
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">Ruhezeiten</h2>
          </div>
          
          <p className="text-gray-600 mb-4">
            Während der Ruhezeiten werden keine Benachrichtigungen gesendet
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">
                Von
              </label>
              <input
                type="time"
                value={backendSettings.quiet_hours.from}
                onChange={(e) => handleQuietHoursChange('from', e.target.value)}
                className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl text-2xl font-bold text-center focus:border-green-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">
                Bis
              </label>
              <input
                type="time"
                value={backendSettings.quiet_hours.to}
                onChange={(e) => handleQuietHoursChange('to', e.target.value)}
                className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl text-2xl font-bold text-center focus:border-green-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* P2: Clock Display Settings */}
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-3xl p-4 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🕐 Uhr & Anzeige</h2>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-8 h-8 text-cyan-600" />
            <h2 className="text-2xl font-bold text-gray-900">Uhrgröße</h2>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { value: 'small', label: 'Klein' },
              { value: 'medium', label: 'Mittel' },
              { value: 'large', label: 'Groß' }
            ].map((size) => (
              <button
                key={size.value}
                onClick={() => {
                  updateSetting('clock', {
                    ...settings.clock,
                    size: size.value
                  });
                  toast.success(`Uhrgröße: ${size.label}`);
                }}
                className={`p-6 rounded-xl text-center font-bold transition-all ${
                  settings.clock?.size === size.value
                    ? 'bg-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className={`${
                  size.value === 'small' ? 'text-2xl' :
                  size.value === 'medium' ? 'text-3xl' :
                  'text-4xl'
                } font-bold mb-1`}>
                  {size.value === 'small' ? '🕐' : size.value === 'medium' ? '🕑' : '🕒'}
                </div>
                <div className="text-lg">{size.label}</div>
              </button>
            ))}
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
              <div>
                <div className="text-lg font-bold text-gray-900">Sekunden anzeigen</div>
                <div className="text-sm text-gray-600">Live-Sekunden in der Uhr</div>
              </div>
              <button
                onClick={() => {
                  updateSetting('clock', {
                    ...settings.clock,
                    showSeconds: !settings.clock?.showSeconds
                  });
                  toast.success(settings.clock?.showSeconds ? 'Sekunden ausgeblendet' : 'Sekunden eingeblendet');
                }}
                className={`relative w-16 h-8 rounded-full transition-colors ${
                  settings.clock?.showSeconds ? 'bg-cyan-500' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  settings.clock?.showSeconds ? 'translate-x-8' : 'translate-x-0'
                }`} />
              </button>
            </div>
            
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
              <div>
                <div className="text-lg font-bold text-gray-900">Datum anzeigen</div>
                <div className="text-sm text-gray-600">Wochentag und Datum unter der Uhr</div>
              </div>
              <button
                onClick={() => {
                  updateSetting('clock', {
                    ...settings.clock,
                    showDate: !settings.clock?.showDate
                  });
                  toast.success(settings.clock?.showDate ? 'Datum ausgeblendet' : 'Datum eingeblendet');
                }}
                className={`relative w-16 h-8 rounded-full transition-colors ${
                  settings.clock?.showDate ? 'bg-cyan-500' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  settings.clock?.showDate ? 'translate-x-8' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* P2: Screensaver Settings */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-3xl p-4 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🖼️ Bildschirmschoner</h2>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Moon className="w-8 h-8 text-indigo-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Bildschirmschoner aktivieren</h2>
                <p className="text-lg text-gray-600">Zeigt Uhr und Fotos bei Inaktivität</p>
              </div>
            </div>
            
            <button
              onClick={() => {
                updateSetting('screensaver', {
                  ...settings.screensaver,
                  enabled: !settings.screensaver?.enabled
                });
                toast.success(settings.screensaver?.enabled ? 'Bildschirmschoner deaktiviert' : 'Bildschirmschoner aktiviert');
              }}
              className={`relative w-20 h-10 rounded-full transition-colors ${
                settings.screensaver?.enabled ? 'bg-indigo-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-1 left-1 w-8 h-8 bg-white rounded-full transition-transform ${
                settings.screensaver?.enabled ? 'translate-x-10' : 'translate-x-0'
              }`} />
            </button>
          </div>
          
          {settings.screensaver?.enabled && (
            <div className="space-y-4 pt-4 border-t-2 border-gray-100">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-3">
                  Starten nach (Minuten)
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {[1, 3, 5, 10].map((min) => (
                    <button
                      key={min}
                      onClick={() => {
                        updateSetting('screensaver', {
                          ...settings.screensaver,
                          startAfterMinutes: min
                        });
                        toast.success(`Bildschirmschoner nach ${min} Min.`);
                      }}
                      className={`p-4 rounded-xl text-center font-bold transition-all ${
                        settings.screensaver?.startAfterMinutes === min
                          ? 'bg-indigo-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <div className="text-2xl font-bold">{min}</div>
                      <div className="text-xs">Min</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-indigo-50 rounded-xl border-2 border-indigo-200">
                <p className="text-gray-700">
                  ℹ️ Der Bildschirmschoner zeigt die Uhrzeit und kann später mit Familienfotos erweitert werden.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Save Changes Banner (Sticky) */}
        {hasChanges && (
          <div className="sticky bottom-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="text-white">
                <div className="text-xl font-bold">Sie haben Änderungen vorgenommen</div>
                <div className="text-sm opacity-90">Speichern Sie, um die Einstellungen zu übernehmen</div>
              </div>
              <button
                onClick={saveToBackend}
                disabled={saving}
                className="bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Jetzt speichern
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          onClick={() => {
            if (window.confirm('Möchten Sie alle Einstellungen zurücksetzen?')) {
              resetSettings();
              toast.success('Einstellungen zurückgesetzt');
            }
          }}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl p-6 text-xl font-bold transition-all"
        >
          Einstellungen zurücksetzen
        </button>
      </div>
    </div>
  );
}
